import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase/client';
import { useBusinessConfig } from '@/hooks/useBusinessConfig';
import {
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  ArrowRight,
  Loader2,
  Clock,
  Activity,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StaffTimeclock } from './vbs/forms/StaffTimeclock';
import { QuotingEngine } from './vbs/forms/QuotingEngine';
import { EmergencyContactsManager } from './EmergencyContactsManager';
import { InvoiceManager } from './InvoiceManager';
import { HomeMode } from './HomeMode';
import { InventoryManager } from './vbs/forms/InventoryManager';
import { GlobalSettingsPage } from './GlobalSettingsPage';
import { TaskTemplateEngine } from './vbs/forms/TaskTemplateEngine';
import { CustomerCRM } from './vbs/dashboard/CustomerCRM';
import { ServicesManager } from './vbs/forms/ServicesManager';
import { BookingsManager } from './vbs/dashboard/BookingsManager';

interface AuditLog {
  id: string;
  event_type: string;
  created_at: string;
  meta: {
    item_name?: string;
    medication?: string;
    action?: string;
    prev_balance?: number;
    new_balance?: number;
  };
}

interface InventoryItem {
  id: string;
  item_name: string;
  current_stock: number;
  reorder_point: number;
}

interface Booking {
  id: string;
  name: string;
  service_date: string;
  status: string;
}

interface TimeEntry {
  quoteId: string;
  startTime: string;
  endTime?: string;
}

interface Quote {
  id: string;
  client_name: string;
  total_amount: number;
  status: string;
  created_at: string;
}

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { config } = useBusinessConfig();
  const [activeView, setActiveView] = useState<'overview' | 'customers' | 'inventory' | 'quotes' | 'invoices' | 'timeclock' | 'quoting' | 'tasks' | 'emergency' | 'homemode' | 'settings' | 'services' | 'bookings'>('overview');
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [logRes, invRes, quoteRes, bookingRes] = await Promise.all([
        supabase.from('audit_logs').select('*').order('created_at', { ascending: false }).limit(10),
        supabase.from('inventory').select('*').order('item_name', { ascending: true }),
        supabase.from('quotes').select('*').order('created_at', { ascending: false }),
        supabase.from('bookings').select('*').order('service_date', { ascending: true }),
      ]);

      if (logRes.data) setLogs(logRes.data as AuditLog[]);
      if (invRes.data) setInventory(invRes.data as InventoryItem[]);
      if (quoteRes.data) setQuotes(quoteRes.data as Quote[]);
      if (bookingRes.data) setBookings(bookingRes.data as Booking[]);
      
      // Load time entries from localStorage
      const storedTime = localStorage.getItem('timeEntries');
      if (storedTime) {
        setTimeEntries(JSON.parse(storedTime));
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 space-y-4">
      <Loader2 className="animate-spin text-purple-600 w-12 h-12" />
      <p className="font-black uppercase italic tracking-widest text-slate-400 text-xs">Syncing Business Vault...</p>
    </div>
  );

  return (
    <div className="space-y-6 pb-24">
      {/* Trial Expiry Banner */}
      {config && config.subscription_status === 'trial' && config.trial_ends_at && (() => {
        const trialDaysLeft = Math.max(0, Math.ceil((new Date(config.trial_ends_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
        if (trialDaysLeft <= 7 && trialDaysLeft > 0) {
          return (
            <div className="bg-amber-50 border-b-2 border-amber-200 px-4 py-3 text-center rounded-2xl">
              <p className="text-amber-800 font-black text-xs uppercase">
                ⚡ Trial expires in {trialDaysLeft} days —
                <button onClick={() => navigate('/upgrade')} className="underline ml-1 font-black">Upgrade Now</button>
              </p>
            </div>
          );
        }
        return null;
      })()}

      {/* Tab Navigation */}
      <div className="flex bg-white p-1 rounded-2xl border-2 border-slate-100 shadow-sm overflow-x-auto">
        {(['overview', 'customers', 'inventory', 'quotes', 'invoices', 'timeclock', 'quoting', 'tasks', 'services', 'bookings', 'emergency', 'homemode', 'settings'] as const).map((view) => (
          <button
            key={view}
            onClick={() => setActiveView(view)}
            className={`flex-1 min-w-[80px] py-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all ${
              activeView === view
                ? 'bg-slate-900 text-white shadow-lg'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {view === 'emergency' ? 'SOS' : view === 'homemode' ? 'Home' : view === 'settings' ? 'Config' : view === 'invoices' ? 'Invoices' : view === 'tasks' ? 'Tasks' : view === 'customers' ? 'Customers' : view === 'inventory' ? 'Inventory' : view === 'services' ? 'Services' : view === 'bookings' ? 'Bookings' : view.charAt(0).toUpperCase() + view.slice(1)}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {activeView === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
              <TrendingUp className="text-purple-600 mb-2 w-5 h-5" />
              <p className="text-[10px] font-black text-slate-400 uppercase">Total Quoted</p>
              <p className="text-2xl font-black italic text-slate-900">
                ${quotes.reduce((acc, q) => acc + Number(q.total_amount), 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
              <AlertTriangle
                className={`${
                  inventory.some(i => i.current_stock <= i.reorder_point)
                    ? 'text-red-500'
                    : 'text-slate-300'
                } mb-2 w-5 h-5`}
              />
              <p className="text-[10px] font-black text-slate-400 uppercase">Low Stock</p>
              <p className="text-2xl font-black italic text-slate-900">
                {inventory.filter(i => i.current_stock <= i.reorder_point).length}
              </p>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
              <Clock className="text-teal-600 mb-2 w-5 h-5" />
              <p className="text-[10px] font-black text-slate-400 uppercase">Today's Bookings</p>
              <p className="text-2xl font-black italic text-slate-900">
                {bookings.filter(b => {
                  const today = new Date().toISOString().split('T')[0];
                  return b.service_date === today;
                }).length}
              </p>
            </div>
            <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
              <Activity className="text-amber-600 mb-2 w-5 h-5" />
              <p className="text-[10px] font-black text-slate-400 uppercase">Hours This Week</p>
              <p className="text-2xl font-black italic text-slate-900">
                {(() => {
                  const now = new Date();
                  const startOfWeek = new Date(now);
                  startOfWeek.setDate(now.getDate() - now.getDay());
                  startOfWeek.setHours(0, 0, 0, 0);
                  
                  const weekEntries = timeEntries.filter(e => {
                    const entryDate = new Date(e.startTime);
                    return entryDate >= startOfWeek && e.endTime;
                  });
                  
                  const totalMs = weekEntries.reduce((acc, e) => {
                    return acc + (new Date(e.endTime!).getTime() - new Date(e.startTime).getTime());
                  }, 0);
                  
                  return `${(totalMs / (1000 * 60 * 60)).toFixed(1)}h`;
                })()}
              </p>
            </div>
          </div>

          <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white shadow-xl relative overflow-hidden">
            <div className="flex items-center gap-2 mb-6">
              <ClipboardList className="text-purple-400 w-5 h-5" />
              <h3 className="font-black uppercase italic tracking-tighter text-xl">Immutable Audit Feed</h3>
            </div>
            <div className="space-y-4">
              {logs.map((log) => (
                <div key={log.id} className="flex gap-4 border-b border-slate-800 pb-4 last:border-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-1" />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="text-[9px] font-black uppercase text-slate-500">
                        {new Date(log.created_at).toLocaleTimeString()}
                      </p>
                      <span className="text-[7px] font-black bg-slate-800 px-2 py-0.5 rounded text-purple-400">
                        AUDIT_{log.id.slice(0,4)}
                      </span>
                    </div>
                    <p className="font-bold text-xs mt-1 uppercase">
                      {log.event_type.replace(/_/g, ' ')}: 
                      <span className="text-emerald-400"> {log.meta.item_name || log.meta.medication}</span>
                    </p>
                    <p className="text-[9px] text-slate-500 font-bold uppercase mt-1">
                      Balance: {log.meta.prev_balance} → {log.meta.new_balance}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMERS */}
      {activeView === 'customers' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <CustomerCRM />
        </div>
      )}

      {/* INVENTORY */}
      {activeView === 'inventory' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <InventoryManager />
        </div>
      )}

      {/* QUOTES - FIXED BUTTON */}
      {activeView === 'quotes' && (
        <div className="space-y-4">
          <h3 className="font-black uppercase italic text-xl tracking-tighter px-2 text-slate-900">Quote Ledger</h3>
          <div className="grid gap-3">
            {quotes.length === 0 && (
              <div className="text-center py-12 text-slate-400 font-bold">
                No quotes yet. Create one in the Quoting tab.
              </div>
            )}
            {quotes.map(quote => (
              <div 
                key={quote.id} 
                className="bg-white border-2 border-slate-100 rounded-[2rem] p-6 shadow-sm hover:border-purple-200 transition-colors"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <span className="text-[8px] font-black bg-purple-50 text-purple-600 px-2 py-0.5 rounded uppercase">
                      {quote.status}
                    </span>
                    <h4 className="text-lg font-black uppercase text-slate-900 mt-1 leading-none">
                      {quote.client_name}
                    </h4>
                    <p className="text-[9px] font-bold text-slate-400 uppercase mt-1">
                      {new Date(quote.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black italic text-slate-900">
                      ${Number(quote.total_amount).toLocaleString()}
                    </p>
                    <p className="text-[8px] font-black text-slate-400 uppercase">Inc. GST</p>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full rounded-xl border-slate-100 font-black uppercase text-[10px] gap-2 h-10 group"
                  onClick={() => window.open(`/quote/${quote.id}`, '_blank')}
                >
                  View Proposal 
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TIMECLOCK */}
      {activeView === 'timeclock' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <StaffTimeclock />
        </div>
      )}

      {/* QUOTING ENGINE */}
      {activeView === 'quoting' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <QuotingEngine />
        </div>
      )}

      {/* TASK TEMPLATES */}
      {activeView === 'tasks' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <TaskTemplateEngine />
        </div>
      )}

      {/* SERVICES */}
      {activeView === 'services' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <ServicesManager />
        </div>
      )}

      {/* BOOKINGS */}
      {activeView === 'bookings' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <BookingsManager />
        </div>
      )}

      {/* INVOICES */}
      {activeView === 'invoices' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <InvoiceManager />
        </div>
      )}

      {/* EMERGENCY CONTACTS */}
      {activeView === 'emergency' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <EmergencyContactsManager />
        </div>
      )}

      {/* HOME MODE */}
      {activeView === 'homemode' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <HomeMode />
        </div>
      )}

      {/* GLOBAL SETTINGS */}
      {activeView === 'settings' && (
        <div className="bg-white p-6 rounded-[2rem] border-2 border-slate-100 shadow-sm">
          <GlobalSettingsPage />
        </div>
      )}
    </div>
  );
};